import { useCallback, useMemo, useState } from 'react'
import type { PresentationPhotoSource } from '../diagnostics/planPresentation'

export function useGammaPhotoSelection(initialIds: string[] = []) {
  const [selectedIds, setSelectedIds] = useState<string[]>(initialIds)

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const toggle = useCallback((photoId: string) => {
    setSelectedIds((prev) =>
      prev.includes(photoId) ? prev.filter((id) => id !== photoId) : [...prev, photoId],
    )
  }, [])

  const setGroupSelection = useCallback((photos: PresentationPhotoSource[], selected: boolean) => {
    const ids = photos.map((p) => p.photo_id)
    setSelectedIds((prev) => {
      const set = new Set(prev)
      for (const id of ids) {
        if (selected) set.add(id)
        else set.delete(id)
      }
      return [...set]
    })
  }, [])

  const selectAll = useCallback((photos: PresentationPhotoSource[]) => {
    setSelectedIds(photos.map((p) => p.photo_id))
  }, [])

  const clearAll = useCallback(() => setSelectedIds([]), [])

  const resetSelection = useCallback((ids: string[]) => {
    setSelectedIds(ids)
  }, [])

  const isGroupFullySelected = useCallback(
    (photos: PresentationPhotoSource[]) =>
      photos.length > 0 && photos.every((p) => selectedSet.has(p.photo_id)),
    [selectedSet],
  )

  return {
    selectedIds,
    selectedSet,
    selectedCount: selectedIds.length,
    toggle,
    setGroupSelection,
    selectAll,
    clearAll,
    isGroupFullySelected,
    resetSelection,
  }
}
